import React from 'react';
import {
	createStyles,
	makeStyles,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { formatInTimeZone } from 'date-fns-tz';
import { CaseCommentDetails, CaseCommentHistory } from '@data/caseComments/types/CaseComments.types';
import { CommentsTableHeader } from '@components/CaseDetails/CommentsSection/CommentsTable/CommentsTableHeader/CommentsTableHeader';
import { getDateFromApiDateTime } from '@calendar';
import { CaseDetailsInfoSelector } from '@store/selectors/CaseDetailsSelector';
import { useCollectionEntity } from '@utilities/hooks/useCollectionEntity/useCollectionEntity';
import { Unit } from '@data/unit/Unit';
import { ENDPOINT_USER_UNITS } from '@utilities/apiConstants';
import { CommentMenu } from '@components/CaseDetails/CommentsSection/CommentsTable/CommentMenu/CommentMenu';
import { useCommentsContext } from '@components/CaseDetails/CommentsSection/CommentsContext/CommentsContext';
import {
	useCaseDetailsPageContext
} from '@components/CaseDetails/CaseDetailsContext/CaseDetailsPageContext/CaseDetailsPageContext';
import { formatCommentTypeString } from '@components/CaseDetails/CommentsSection/utilities/commentUtils';
import classNames from 'classnames';
import { CASE_COMMENT_EDITED_LABEL } from '@utilities/constants';

const useStyles = makeStyles((theme) =>
	createStyles({
		tableContainer: {
			'padding': theme.spacing(0),
			'width': 'unset',
			'& > table > tbody > tr:last-child': {
				borderBottom: 'none',
			},
			'& > table > thead > tr > th': {
				'backgroundColor': theme.palette.primary[800],
				'borderBottomColor': 'rgba(0, 0, 0, 0.12)',
				'color': theme.palette.common.white,
				'fontWeight': 600,
				'letterSpacing': '0.15px',
				'lineHeight': '1.5rem',
				'paddingLeft': theme.spacing(2),
				'& > svg': {
					color: theme.palette.common.white,
				}
			},
			'& > table > tbody > thead > tr > th.MuiTableCell-head': {
				borderBottom: 'none',
				paddingBottom: 0,
			},
			'& > table > tbody > tr:not($commentHeader) > td': {
				fontWeight: 400,
				letterSpacing: '0.25px',
				lineHeight: '1.5rem',
			},
		},
		tableContainerCondensed: {
			'& > table > tbody': {
				overflowY: 'auto',
			},
			'& > table > tbody > tr:not($commentRow, $commentHeader) > td': {
				borderBottom: 'none',
				padding: theme.spacing(2, 2, 0, 2),
			},
		},
		rowWithBorder: {
			borderBottom: '1px solid rgba(33, 33, 33, 0.08)',
		},
		editText: {
			color: theme.palette.grey[600],
			fontSize: '0.8rem',
			marginTop: theme.spacing(0),
		},
		historyText: {
			textDecoration: 'line-through',
		},
		editedHistoryRow: {
			'backgroundColor': '#FFFAEE',
			'& > td:first-of-type': {
				paddingLeft: theme.spacing(2),
				boxShadow: `inset 12px 0 ${theme.palette.grey[300]}`,
			},
		},
		deletedHistoryRow: {
			backgroundColor: '#FDE6EE',
		},
		createdDateTime: {
			fontSize: '0.875rem',
			fontWeight: 400,
			whiteSpace: 'nowrap',
			marginBottom: theme.spacing(0),
		},
		commentHeader: {
			'& > td': {
				color: theme.palette.primary[800],
				fontWeight: 600,
				paddingBottom: 0,
				borderBottom: 'none',
			},
		},
		commentRow: {
			'& > td': {
				paddingTop: theme.spacing(0),
				borderBottom: 'none',
			},
			'& > td > div': {
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				width: '100%',
			},
			'& > td > div > div:nth-of-type(1)': {
				width: '95%',
			}
		}
	})
);

const formatDateAndTime = (dateTime: string, timeZone: string): string | null => {
	return formatInTimeZone(getDateFromApiDateTime(dateTime), timeZone, 'MM/dd/yyyy h:mm aa');
};

interface CommentsTableRowProps {
	history: CaseCommentHistory;
	comment: CaseCommentDetails;
	hospitalTimeZone?: string;
}

const CommentTableRow: React.FC<CommentsTableRowProps> = ({ comment, hospitalTimeZone, history }) => {
	const classes = useStyles();
	const { showCondensedView } = useCaseDetailsPageContext();

	return (
		<React.Fragment>
			<TableRow className={classNames({
				[classes.editedHistoryRow]: history.status === 'EDITED' || history.status === 'CREATED',
			})}>
				<TableCell>
					<Typography className={classes.createdDateTime}>
						{hospitalTimeZone && formatDateAndTime(comment.createdDateTime, hospitalTimeZone)}
					</Typography>
					{history.status === 'EDITED' && hospitalTimeZone && (
						<Typography className={classes.editText}>
							(edited on {formatDateAndTime(history.modifiedDateTime, hospitalTimeZone)})
						</Typography>
					)}
					{history.status === 'CREATED' && hospitalTimeZone && (
						<Typography className={classes.editText}>
							(edited on {formatDateAndTime(history.modifiedDateTime, hospitalTimeZone)})
						</Typography>
					)}
				</TableCell>
				<TableCell className={classes.historyText}>
					{formatCommentTypeString(history.types)}
				</TableCell>
				<TableCell className={classes.historyText}>
					{comment.commenterDisplay}
				</TableCell>
				{!showCondensedView &&
					<>
						<TableCell className={classes.historyText} colSpan={2}>
							{history.comment}
						</TableCell>
					</>
				}
			</TableRow>
			{showCondensedView &&
				<React.Fragment>
					<TableRow className={classNames(classes.commentHeader, {
						[classes.editedHistoryRow]: history.status === 'EDITED' || history.status === 'CREATED'
					})}>
						<TableCell colSpan={3}>Comment</TableCell>
					</TableRow>
					<TableRow className={classNames(classes.rowWithBorder, classes.commentRow, {
						[classes.editedHistoryRow]: history.status === 'EDITED' || history.status === 'CREATED'
					})}>
						<TableCell colSpan={3} className={classes.historyText}>
							{history.comment}
						</TableCell>
					</TableRow>
				</React.Fragment>
			}
		</React.Fragment>
	);
};

export const CommentsTable: React.FC = () => {
	const classes = useStyles();
	const { comments, setSelectedComment, showHistory } = useCommentsContext();
	const caseInfo = useSelector(CaseDetailsInfoSelector);
	const { showCondensedView } = useCaseDetailsPageContext();
	const { unit } = caseInfo;
	const { hospitalTimeZone } = useCollectionEntity<Unit>(ENDPOINT_USER_UNITS, unit, 'id');

	const handleMenuClick = React.useCallback((comment: CaseCommentDetails) => {
		setSelectedComment(comment);
	}, [setSelectedComment]);

	return (
		<TableContainer className={classNames(classes.tableContainer, { [classes.tableContainerCondensed]: showCondensedView })}>
			<Table>
				<CommentsTableHeader />
				<TableBody>
					{comments?.length > 0 && (
						comments.map((comment) => {
							const hasEditedHistory = comment.history.some(
								(h) => h.status === 'EDITED'
							);

							return (
								<React.Fragment key={comment.id}>
									{(comment.status !== 'DELETED' ||
										(comment.status === 'DELETED' && showHistory)) && (
										<React.Fragment>
											<TableRow
												className={classNames({
													[classes.deletedHistoryRow]:
													comment.status === 'DELETED',
												})}
											>
												<TableCell>
													<Typography className={classes.createdDateTime}>
														{hospitalTimeZone &&
															formatDateAndTime(
																comment.createdDateTime,
																hospitalTimeZone
															)}
													</Typography>
													{comment.status === 'EDITED' && (
														<Typography className={classes.editText}>
															{CASE_COMMENT_EDITED_LABEL}
														</Typography>
													)}
													{comment.status === 'DELETED' && hospitalTimeZone && (
														<Typography className={classes.editText}>
															(deleted on {formatDateAndTime(
																comment.modifiedDateTime,
																hospitalTimeZone
															)}
															)
														</Typography>
													)}
												</TableCell>
												<TableCell
													className={classNames({
														[classes.historyText]:
														comment.status === 'DELETED',
													})}
												>
													{comment.typesDisplay}
												</TableCell>
												<TableCell
													className={classNames({
														[classes.historyText]:
														comment.status === 'DELETED',
													})}
												>
													{comment.commenterDisplay}
												</TableCell>
												{!showCondensedView &&
													<>
														<TableCell className={classNames({
															[classes.historyText]:
															comment.status === 'DELETED',
														})}>{comment.comment}</TableCell>
														<TableCell>
															{comment.isOwner && comment.status !== 'DELETED' && (
																<CommentMenu onClick={() => handleMenuClick(comment)} />
															)}
														</TableCell>
													</>
												}
											</TableRow>
											{showCondensedView &&
												<React.Fragment>
													<TableRow className={classNames(classes.commentHeader, {
														[classes.deletedHistoryRow]:
														comment.status === 'DELETED',
													})}>
														<TableCell colSpan={3}>Comment</TableCell>
													</TableRow>
													<TableRow className={classNames(classes.rowWithBorder, classes.commentRow, {
														[classes.deletedHistoryRow]:
														comment.status === 'DELETED',
													})}>
														<TableCell colSpan={3}>
															<div>
																<div className={classNames({
																	[classes.historyText]:
																	comment.status === 'DELETED',
																})}>{comment.comment}</div>
																<div>{comment.isOwner && comment.status !== 'DELETED' && <CommentMenu onClick={()=> handleMenuClick(comment)}/>}</div>
															</div>
														</TableCell>
													</TableRow>
												</React.Fragment>
											}
										</React.Fragment>
									)}
									{showHistory &&
										(comment.status !== 'DELETED' ||
											(comment.status === 'DELETED' && hasEditedHistory)) &&
										comment.history
											.filter((history, index) => (!(index === 0 && comment.history[index].status === 'EDITED' && comment.status === 'DELETED')))
											.map((history) => (
												<CommentTableRow
													key={history.modifiedDateTime}
													comment={comment}
													history={history}
													hospitalTimeZone={hospitalTimeZone}
												/>
											))}
								</React.Fragment>
							);
						})
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
